import React, { useEffect, useState } from "react";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    Select,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment";
import ConfirmDialog from "../components/ConfirmDialog";
import CustomSnackbar from "../components/CustomSnackbar";
import AdminNotificationService from "./AdminNotificationService";
import NotificationOverviewPanel from "./NotificationOverviewPanel";

/** Client-only message presets (not stored in the database). */
const MESSAGE_PRESETS = [
    {
        id: "daily-reward",
        label: "Daily reward",
        title: "🎁 Your daily reward awaits!",
        body: "Open the app and claim today's bonus before it resets.",
    },
    {
        id: "new-puzzle",
        label: "New puzzle",
        title: "🧩 A new puzzle is ready",
        body: "Tap in and see if you can solve it before anyone else.",
    },
    {
        id: "streak",
        label: "Keep streak",
        title: "🔥 Don't break your streak!",
        body: "One quick play keeps your streak alive — jump back in now.",
    },
    {
        id: "challenge-waiting",
        label: "Challenge waiting",
        title: "⭐ Your next challenge is waiting",
        body: "A fresh puzzle is lined up for you. Ready when you are.",
    },
    {
        id: "leaderboard",
        label: "Leaderboard",
        title: "🏆 Climb the leaderboard",
        body: "Play now and move up the ranks before the day ends.",
    },
    {
        id: "come-back",
        label: "Come back",
        title: "✨ We saved your spot",
        body: "Your progress is waiting — finish today's puzzle when you're ready.",
    },
    {
        id: "one-more",
        label: "One more",
        title: "🎯 Just one more puzzle…",
        body: "You're so close. Open the app and knock out another round.",
    },
    {
        id: "fresh-drop",
        label: "Fresh drop",
        title: "🆕 Fresh puzzles just dropped",
        body: "New boards are live. Be among the first to solve them.",
    },
];

const KIND_LABELS = {
    ADMIN: "Admin",
    PUBLISH: "Puzzle published",
    TEST: "Test",
    DAILY_GIFT: "Daily gift",
};

const TAB_OVERVIEW = "overview";
const TAB_APPS = "apps";
const TAB_LOGS = "logs";
const VALID_TABS = [TAB_OVERVIEW, TAB_APPS, TAB_LOGS];
const TAB_STORAGE_KEY = "admin.notification.tab.v2";

function loadStoredTab() {
    try {
        const v = window.localStorage.getItem(TAB_STORAGE_KEY);
        if (VALID_TABS.includes(v)) return v;
        // Previous numeric tabs: 0 Apps, 1 Logs.
        if (v === "1") return TAB_LOGS;
    } catch {}
    return TAB_OVERVIEW;
}

function kindChip(kind) {
    const label = KIND_LABELS[kind] || kind || "—";
    let color = "default";
    if (kind === "ADMIN") color = "primary";
    if (kind === "PUBLISH") color = "success";
    if (kind === "TEST") color = "default";
    return <Chip size="small" label={label} color={color} variant="outlined" />;
}

function notificationUserCounts(row) {
    const sent = Number(row?.sentUserCount ?? row?.sentCount) || 0;
    const recipients = Number(row?.recipientUserCount ?? row?.recipientCount) || 0;
    return { sent, recipients };
}

function formatNotificationResult(row) {
    const { sent, recipients } = notificationUserCounts(row);
    const userText = recipients <= 0
        ? `${sent} user${sent === 1 ? "" : "s"}`
        : `${sent} of ${recipients} user${recipients === 1 ? "" : "s"}`;
    const devices = Number(row?.recipientCount) || 0;
    const sentDevices = Number(row?.sentCount) || 0;
    const sns = Number(row?.snsEndpointCount) || 0;
    if (devices <= 0 && sns <= 0) {
        return userText;
    }
    let text = `${userText} · ${sentDevices} of ${devices} device${devices === 1 ? "" : "s"}`;
    if (sns > 0) {
        const enabled = Number(row?.snsEnabledEndpointCount);
        text += ` · ${sns} SNS`;
        if (Number.isFinite(enabled) && enabled >= 0) {
            text += ` (${enabled} on)`;
        }
    }
    return text;
}

const csvCell = (value) => {
    if (value == null) return "";
    const text = String(value);
    if (/[",\n\r]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
};

const downloadCsv = (filename, headers, rows) => {
    const lines = [headers, ...rows].map((row) => row.map(csvCell).join(","));
    const blob = new Blob([`\uFEFF${lines.join("\r\n")}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

const LOG_CSV_HEADERS = [
    "id",
    "kind",
    "app",
    "title",
    "message",
    "result",
    "devices",
    "sentDevices",
    "failed",
    "pushOn",
    "sent",
];

function logCsvRow(row) {
    const sentAt = row?.createdAt != null ? moment(row.createdAt).format("YYYY-MM-DD HH:mm:ss") : "";
    return [
        row?.id ?? "",
        KIND_LABELS[row?.kind] || row?.kind || "",
        row?.appCode ?? "",
        row?.title ?? "",
        row?.body ?? "",
        formatNotificationResult(row),
        Number(row?.recipientCount) || 0,
        Number(row?.sentCount) || 0,
        Number(row?.failedCount) || 0,
        row?.pushEnabled ? "Yes" : "No",
        sentAt,
    ];
}

function getLogColumns() {
    return [
        {
            field: "kind",
            headerName: "Kind",
            width: 130,
            renderCell: (params) => kindChip(params.value),
        },
        {
            field: "appCode",
            headerName: "App",
            width: 120,
        },
        {
            field: "title",
            headerName: "Title",
            flex: 1,
            minWidth: 100,
        },
        {
            field: "body",
            headerName: "Message",
            flex: 1,
            minWidth: 120,
        },
        {
            field: "result",
            headerName: "Result",
            width: 240,
            sortable: false,
            align: "center",
            headerAlign: "center",
            valueGetter: (_value, row) => formatNotificationResult(row),
            renderCell: (params) => {
                const { sent } = notificationUserCounts(params.row);
                return (
                    <Chip
                        size="small"
                        label={formatNotificationResult(params.row)}
                        color={sent > 0 ? "success" : "default"}
                        variant={sent > 0 ? "filled" : "outlined"}
                    />
                );
            },
        },
        {
            field: "recipientCount",
            headerName: "Devices",
            width: 100,
            type: "number",
            align: "center",
            headerAlign: "center",
        },
        {
            field: "sentCount",
            headerName: "Sent devices",
            width: 120,
            type: "number",
            align: "center",
            headerAlign: "center",
        },
        {
            field: "failedCount",
            headerName: "Failed",
            width: 90,
            type: "number",
            align: "center",
            headerAlign: "center",
        },
        {
            field: "pushEnabled",
            headerName: "Push on",
            width: 100,
            renderCell: (params) => (
                <Chip
                    size="small"
                    label={params.value ? "Yes" : "No"}
                    color={params.value ? "success" : "warning"}
                    variant="outlined"
                />
            ),
        },
        {
            field: "createdAt",
            headerName: "Sent",
            width: 170,
            valueGetter: (value, row) => row.createdAt,
            valueFormatter: (value) => (value != null ? moment(value).format("YYYY-MM-DD HH:mm:ss") : ""),
        },
    ];
}

export default function AdminNotificationMain() {
    const [apps, setApps] = useState([]);
    const [pushEnabled, setPushEnabled] = useState(null);
    const [selectedAppCode, setSelectedAppCode] = useState("");
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [selectedPresetId, setSelectedPresetId] = useState(null);
    const [presetDialogOpen, setPresetDialogOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
    const [totalRows, setTotalRows] = useState(0);
    const [logAppCode, setLogAppCode] = useState("");
    const [tabValue, setTabValue] = useState(loadStoredTab);
    const [selectedLogIds, setSelectedLogIds] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });

    const selectedApp = apps.find((a) => a.code === selectedAppCode);

    const showSnack = (severity, message) => setSnackbar({ open: true, severity, message });

    const loadApps = async () => {
        const result = await AdminNotificationService.listApps();
        if (result.success && result.data) {
            const list = Array.isArray(result.data.content) ? result.data.content : [];
            setApps(list);
            setPushEnabled(typeof result.data.pushEnabled === "boolean" ? result.data.pushEnabled : null);
        } else {
            setApps([]);
            showSnack("error", result.error || "Failed to load apps");
        }
    };

    const fetchLogs = async () => {
        setLoading(true);
        const result = await AdminNotificationService.listNotifications(
            paginationModel.page,
            paginationModel.pageSize,
            "createdAt:desc",
            logAppCode,
        );
        setLoading(false);
        if (result.success && result.data) {
            const data = result.data;
            const content = data.content ?? data;
            const list = Array.isArray(content) ? content : [];
            setRows(list);
            const total = data.page?.totalElements ?? data.totalElements ?? list.length;
            setTotalRows(typeof total === "number" ? total : 0);
        } else {
            setRows([]);
            setTotalRows(0);
            showSnack("error", result.error || "Failed to load notification logs");
        }
    };

    useEffect(() => {
        loadApps();
    }, []);

    useEffect(() => {
        fetchLogs();
    }, [paginationModel.page, paginationModel.pageSize, logAppCode]);

    const handleSelectApp = (code) => {
        setSelectedAppCode(code);
        setLogAppCode(code);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    };

    const handleSendClick = () => {
        if (!selectedAppCode) {
            showSnack("warning", "Select an app");
            return;
        }
        if (!title.trim()) {
            showSnack("warning", "Title is required");
            return;
        }
        if (!body.trim()) {
            showSnack("warning", "Message is required");
            return;
        }
        setConfirmOpen(true);
    };

    const handleSendConfirm = async () => {
        setSending(true);
        const result = await AdminNotificationService.send({
            appCode: selectedAppCode,
            title: title.trim(),
            body: body.trim(),
        });
        setSending(false);
        if (!result.success) {
            showSnack("error", result.error || "Failed to send notification");
            return;
        }
        const data = result.data || {};
        const sentUsers = Number(data.sentUsers ?? data.sent) || 0;
        const recipientUsers = Number(data.recipientUsers ?? data.deviceTokens) || 0;
        const deviceTokens = Number(data.deviceTokens) || 0;
        if (deviceTokens === 0) {
            showSnack("warning", data.message || "No recipients — notification was not sent.");
        } else if (data.pushEnabled === false) {
            showSnack("warning", data.message || "Push delivery is disabled. Message was saved.");
        } else {
            showSnack(
                "success",
                `Sent to ${sentUsers} of ${recipientUsers} user${recipientUsers === 1 ? "" : "s"}.`,
            );
        }
        setTitle("");
        setBody("");
        setSelectedPresetId(null);
        fetchLogs();
        loadApps();
    };

    const handleApplyPreset = (preset) => {
        setTitle(preset.title);
        setBody(preset.body);
        setSelectedPresetId(preset.id);
        setPresetDialogOpen(false);
    };

    const handleLogSelectionChange = (model) => {
        const ids = Array.isArray(model) ? model : model?.ids ? Array.from(model.ids) : [];
        setSelectedLogIds(ids);
    };

    const handleExportLogs = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            const pageSize = 200;
            const collected = [];
            let page = 0;
            let total = totalRows;
            while (collected.length < total) {
                const result = await AdminNotificationService.listNotifications(
                    page,
                    pageSize,
                    "createdAt:desc",
                    logAppCode,
                );
                if (!result.success || !result.data) {
                    showSnack("error", result.error || "Failed to export notification logs");
                    return;
                }
                const data = result.data;
                const content = data.content ?? data;
                const list = Array.isArray(content) ? content : [];
                const reported = data.page?.totalElements ?? data.totalElements;
                if (typeof reported === "number") {
                    total = reported;
                }
                collected.push(...list);
                if (list.length < pageSize) break;
                page += 1;
                if (page > 500) break;
            }
            if (collected.length === 0) {
                showSnack("info", "No logs to export");
                return;
            }
            const fileApp = logAppCode ? `-${logAppCode}` : "";
            downloadCsv(`notification-logs${fileApp}.csv`, LOG_CSV_HEADERS, collected.map(logCsvRow));
        } catch (error) {
            showSnack("error", error?.message || "Failed to export notification logs");
        } finally {
            setExporting(false);
        }
    };

    const handleConfirmDeleteLogs = async () => {
        setDeleteDialogOpen(false);
        const count = selectedLogIds.length;
        setDeleting(true);
        const result = await AdminNotificationService.deleteByIds(selectedLogIds);
        setDeleting(false);
        if (result.success) {
            setSelectedLogIds([]);
            showSnack("success", `${count} log(s) deleted`);
            fetchLogs();
        } else {
            showSnack("error", result.error || "Failed to delete logs");
        }
    };

    const handleTabChange = (_, value) => {
        setTabValue(value);
        try {
            window.localStorage.setItem(TAB_STORAGE_KEY, String(value));
        } catch {}
    };

    const selectedPreset = MESSAGE_PRESETS.find((p) => p.id === selectedPresetId);

    return (
        <Box sx={{ flexGrow: 1, p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
                <NotificationsActiveOutlinedIcon color="action" />
                <Typography variant="h5" sx={{ m: 0 }}>
                    Notification
                </Typography>
                {pushEnabled === false && (
                    <Chip label="Push disabled on server" color="warning" size="small" variant="outlined" />
                )}
            </Box>

            <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
            >
                <Tab value={TAB_OVERVIEW} label="Overview" />
                <Tab value={TAB_APPS} label="Apps" />
                <Tab value={TAB_LOGS} label="Logs" />
            </Tabs>

            {tabValue === TAB_OVERVIEW && <NotificationOverviewPanel />}

            {tabValue === TAB_APPS && (
                <>
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Apps
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Select an app to send a notification to its opted-in users.
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Code</TableCell>
                                    <TableCell align="right">Device tokens</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {apps.map((app) => (
                                    <TableRow
                                        key={app.id || app.code}
                                        hover
                                        selected={app.code === selectedAppCode}
                                        onClick={() => handleSelectApp(app.code)}
                                        sx={{ cursor: "pointer" }}
                                    >
                                        <TableCell>{app.name || "—"}</TableCell>
                                        <TableCell>{app.code || "—"}</TableCell>
                                        <TableCell align="right">{app.deviceTokenCount ?? 0}</TableCell>
                                    </TableRow>
                                ))}
                                {apps.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={3}>No apps found.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Send notification
                    </Typography>
                    {pushEnabled === false && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Push delivery is disabled on the server. The message will still be saved in the log.
                        </Alert>
                    )}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 720 }}>
                        <FormControl size="small" fullWidth>
                            <InputLabel id="notification-app-label">App</InputLabel>
                            <Select
                                labelId="notification-app-label"
                                label="App"
                                value={selectedAppCode}
                                onChange={(e) => handleSelectApp(e.target.value)}
                            >
                                {apps.map((app) => (
                                    <MenuItem key={app.code} value={app.code}>
                                        {app.name} ({app.code})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <Button
                                variant="outlined"
                                startIcon={<PlaylistAddCheckOutlinedIcon />}
                                onClick={() => setPresetDialogOpen(true)}
                            >
                                Presets
                            </Button>
                            {selectedPreset && (
                                <Chip
                                    size="small"
                                    label={`Selected: ${selectedPreset.label}`}
                                    color="primary"
                                    variant="outlined"
                                    onDelete={() => setSelectedPresetId(null)}
                                />
                            )}
                        </Box>
                        <TextField
                            label="Title"
                            size="small"
                            value={title}
                            onChange={(e) => {
                                setTitle(e.target.value);
                                setSelectedPresetId(null);
                            }}
                            inputProps={{ maxLength: 200 }}
                        />
                        <TextField
                            label="Message"
                            size="small"
                            value={body}
                            onChange={(e) => {
                                setBody(e.target.value);
                                setSelectedPresetId(null);
                            }}
                            multiline
                            minRows={3}
                            inputProps={{ maxLength: 2000 }}
                        />
                        <Box>
                            <Button
                                variant="contained"
                                startIcon={<SendOutlinedIcon />}
                                onClick={handleSendClick}
                                disabled={sending || !selectedAppCode}
                            >
                                Send
                            </Button>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
                </>
            )}

            {tabValue === TAB_LOGS && (
                <>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, flexWrap: "wrap", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <Typography variant="h6">Logs</Typography>
                    <Button
                        variant="outlined"
                        startIcon={<FileDownloadOutlinedIcon />}
                        disabled={exporting || loading || totalRows === 0}
                        onClick={handleExportLogs}
                    >
                        {exporting ? "Exporting…" : "Export CSV"}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        disabled={selectedLogIds.length === 0 || deleting}
                        onClick={() => setDeleteDialogOpen(true)}
                    >
                        Delete ({selectedLogIds.length})
                    </Button>
                </Box>
                <FormControl size="small" sx={{ minWidth: 240 }}>
                    <InputLabel id="notification-log-app-label">Filter by app</InputLabel>
                    <Select
                        labelId="notification-log-app-label"
                        label="Filter by app"
                        value={logAppCode}
                        onChange={(e) => {
                            setLogAppCode(e.target.value);
                            setSelectedLogIds([]);
                            setPaginationModel((prev) => ({ ...prev, page: 0 }));
                        }}
                    >
                        <MenuItem value="">All apps</MenuItem>
                        {apps.map((app) => (
                            <MenuItem key={app.code} value={app.code}>
                                {app.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <Box sx={{ width: "100%", minHeight: 400 }}>
                <DataGrid
                    rows={rows}
                    columns={getLogColumns()}
                    getRowId={(row) => row.id}
                    loading={loading}
                    paginationMode="server"
                    rowCount={totalRows}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[10, 25, 50]}
                    checkboxSelection
                    rowSelectionModel={selectedLogIds}
                    onRowSelectionModelChange={handleLogSelectionChange}
                    disableRowSelectionOnClick
                    hideFooterSelectedRowCount
                    autoHeight
                    sx={{ minHeight: 400 }}
                />
            </Box>
                </>
            )}

            <Dialog
                open={presetDialogOpen}
                onClose={() => setPresetDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Select</DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <List disablePadding>
                        {MESSAGE_PRESETS.map((preset) => (
                            <ListItemButton
                                key={preset.id}
                                selected={preset.id === selectedPresetId}
                                onClick={() => handleApplyPreset(preset)}
                                alignItems="flex-start"
                            >
                                <ListItemText
                                    primary={preset.title}
                                    secondary={preset.body}
                                    primaryTypographyProps={{ fontWeight: 600 }}
                                    secondaryTypographyProps={{ sx: { whiteSpace: "pre-wrap" } }}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPresetDialogOpen(false)}>Cancel</Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={deleteDialogOpen}
                setOpen={setDeleteDialogOpen}
                callback={handleConfirmDeleteLogs}
                title="Delete logs"
                message={[
                    `Delete ${selectedLogIds.length} selected log(s)? This cannot be undone.`,
                ]}
                targetName={`${selectedLogIds.length} log(s)`}
            />

            <ConfirmDialog
                open={confirmOpen}
                setOpen={setConfirmOpen}
                title="Send notification?"
                targetName={selectedApp?.name || selectedAppCode}
                message={`This will send to opted-in users of ${selectedApp?.name || selectedAppCode}.`}
                warningAlert={
                    selectedApp
                        ? `${selectedApp.deviceTokenCount ?? 0} registered device token${selectedApp.deviceTokenCount === 1 ? "" : "s"}.`
                        : undefined
                }
                extraContent={
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2">{title}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                            {body}
                        </Typography>
                    </Box>
                }
                callback={handleSendConfirm}
            />

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((s) => ({ ...s, open }))}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </Box>
    );
}
